/**
 * Client Memory API Service
 * Pattern: investorProfileApi.js (class-based singleton with _fetch)
 */

import { authFetch } from '../utils/authFetch';

class ClientMemoryAPI {
  constructor() {
    this.baseURL = import.meta.env.REACT_APP_API_URL || window.location.origin;
    this.apiPrefix = '/api/v1';
  }

  async _fetch(endpoint, options = {}) {
    try {
      const { signal, ...fetchOptions } = options;
      const response = await authFetch(`${this.baseURL}${this.apiPrefix}${endpoint}`, {
        headers: { 'Content-Type': 'application/json', ...fetchOptions.headers },
        signal,
        ...fetchOptions,
      });
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      return await response.json();
    } catch (error) {
      if (error.name === 'AbortError') throw error;
      console.warn(`Memory API call failed: ${endpoint}`, error);
      return null;
    }
  }

  async getProfile(userId) {
    return this._fetch(`/memory/${userId}/profile`);
  }

  async searchMemories(userId, query, limit = 5) {
    return this._fetch(`/memory/${userId}/search?q=${encodeURIComponent(query)}&limit=${limit}`);
  }

  async getRelevant(userId, context, limit = 5) {
    return this._fetch(`/memory/${userId}/relevant?context=${encodeURIComponent(context)}&limit=${limit}`);
  }

  async listMemories(userId, { type, category, limit, offset } = {}) {
    const params = new URLSearchParams();
    if (type) params.set('memory_type', type);
    if (category) params.set('category', category);
    if (limit) params.set('limit', limit);
    if (offset) params.set('offset', offset);
    return this._fetch(`/memory/${userId}?${params}`);
  }

  async createMemory(userId, memory) {
    return this._fetch(`/memory/${userId}`, {
      method: 'POST',
      body: JSON.stringify(memory),
    });
  }

  async deleteMemory(userId, memoryId) {
    return this._fetch(`/memory/${userId}/${memoryId}`, { method: 'DELETE' });
  }

  async syncContext(userId, portfolioData) {
    return this._fetch(`/memory/${userId}/sync`, {
      method: 'POST',
      body: JSON.stringify({ portfolio_data: portfolioData }),
    });
  }

  // ─────────────────────────────────────────────────────────────────────
  // Memory v2 S3 — pattern lifecycle
  // ─────────────────────────────────────────────────────────────────────

  /**
   * List synthesised patterns for the user.
   * Default (no `status`): hides rejected (tombstones the user shouldn't
   * see; only used by synthesis de-emit check). Pass `status=rejected`
   * explicitly to surface them for debugging.
   */
  async listPatterns(userId, status = null) {
    const qs = status ? `?status=${encodeURIComponent(status)}` : '';
    return this._fetch(`/memory/${userId}/patterns${qs}`);
  }

  /**
   * Confirm or reject a synthesised pattern.
   * verdict ∈ {'confirmed', 'rejected'}.
   * Confirmed: pattern surfaces in answers with full authority + decays
   *            at 365d instead of 60d.
   * Rejected:  pattern never surfaces in answers (tombstoned at the RPC
   *            filter); synthesis de-emit check uses it to avoid
   *            re-proposing the same pattern.
   */
  async submitPatternFeedback(userId, patternId, verdict) {
    return this._fetch(`/memory/${userId}/patterns/${patternId}/feedback`, {
      method: 'POST',
      body: JSON.stringify({ verdict }),
    });
  }
}

const clientMemoryApi = new ClientMemoryAPI();
export default clientMemoryApi;
